import { useWheelSettings } from "../context/WheelSettingsContext";
import { BOUNDS, getMaxSpinSoundMuteDurationMs } from "../config/wheelConfig";
import WheelSettingsGraph from "./WheelSettingsGraph";

const SLIDER_FIELDS = [
  { key: "minSpins",        label: "Min Spins",        bounds: BOUNDS.spinCount,     unit: "" },
  { key: "maxSpins",        label: "Max Spins",        bounds: BOUNDS.spinCount,     unit: "" },
  { key: "minDurationMs",   label: "Min Duration",     bounds: BOUNDS.duration,      unit: "ms" },
  { key: "maxDurationMs",   label: "Max Duration",     bounds: BOUNDS.duration,      unit: "ms" },
  { key: "wheelScale",      label: "Wheel Size",       bounds: BOUNDS.wheelScale,    unit: "x" },
  {
    key: "decelerationCoefficient",
    label: "Deceleration Coefficient",
    bounds: BOUNDS.decelerationCoefficient,
    unit: "",
  },
  { key: "winnerDelayMs",   label: "Winner Delay",     bounds: BOUNDS.winnerDelay,   unit: "ms" },
  {
    key: "spinSoundMuteDurationMs",
    label: "Spin Sound Delay",
    unit: "ms",
    getBounds: (settings) => ({
      ...BOUNDS.spinSoundMuteDuration,
      max: getMaxSpinSoundMuteDurationMs(settings),
    }),
  },
];

const TOGGLE_FIELDS = [
  {
    key: "edgeBias",
    label: "Edge Bias",
    helperText: "Favor landing near slice edges for more suspenseful finishes.",
  },
  {
    key: "showQuagganEmojis",
    label: "Show Quaggan Emojis",
    helperText: "Render random quaggan emoji icons next to names and on the wheel.",
  },
  {
    key: "clusterNames",
    label: "Cluster Names",
    helperText: "Combine identical names into one weighted wheel slice and one row in the names panel.",
  },
  {
    key: "showClusterCountControls",
    label: "Show Cluster Count Controls",
    helperText: "Show +/- and quantity input for clustered name counts in the names panel.",
  },
];

export default function SettingsModal({ visible, onClose }) {
  const { settings, setSettings, resetSettings } = useWheelSettings();

  const handleSettingChange = (key) => (event) => {
    const nextValue = event.target.value;
    if (nextValue === "") return;
    setSettings({ [key]: Number(nextValue) });
  };

  const handleToggleChange = (key) => (event) => {
    setSettings({ [key]: event.target.checked });
  };

  if (!visible) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <span style={styles.title}>Wheel Settings</span>
          <button style={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        <div style={styles.body}>
          <div style={styles.controlsColumn}>
            {SLIDER_FIELDS.map(({ key, label, bounds, unit, helperText, getBounds }) => {
              const resolvedBounds = getBounds ? getBounds(settings) : bounds;

              return (
                <div key={key} style={styles.row}>
                  <label style={styles.label}>
                    {label}
                    <span style={styles.value}>
                      {settings[key]}{unit && ` ${unit}`}
                    </span>
                  </label>
                  {helperText && <span style={styles.helperText}>{helperText}</span>}
                  <input
                    type="range"
                    min={resolvedBounds.min}
                    max={resolvedBounds.max}
                    step={resolvedBounds.step}
                    value={settings[key]}
                    onChange={handleSettingChange(key)}
                    style={styles.slider}
                  />
                  <div style={styles.boundsRow}>
                    <span style={styles.boundText}>{resolvedBounds.min}{unit && ` ${unit}`}</span>
                    <span style={styles.boundText}>{resolvedBounds.max}{unit && ` ${unit}`}</span>
                  </div>
                </div>
              );
            })}

            {TOGGLE_FIELDS.map(({ key, label, helperText }) => (
              <div key={key} style={styles.toggleRow}>
                <label style={styles.toggleLabel}>
                  <div style={styles.toggleTextBlock}>
                    <span style={styles.label}>{label}</span>
                    {helperText && <span style={styles.helperText}>{helperText}</span>}
                  </div>
                  <input
                    type="checkbox"
                    checked={Boolean(settings[key])}
                    onChange={handleToggleChange(key)}
                    style={styles.checkbox}
                  />
                </label>
              </div>
            ))}
          </div>

          <WheelSettingsGraph settings={settings} />
        </div>

        <div style={styles.footer}>
          <button style={styles.resetBtn} onClick={resetSettings}>
            Reset to Defaults
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  modal: {
    background: "#232a34",
    borderRadius: 14,
    padding: "24px 28px",
    minWidth: 320,
    maxWidth: 920,
    width: "90vw",
    boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
    color: "#e0e0e0",
    fontFamily: "inherit",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 18,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "#fff",
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#aaa",
    fontSize: 24,
    cursor: "pointer",
    padding: "0 4px",
    lineHeight: 1,
  },
  body: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 20,
  },
  controlsColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    minWidth: 260,
    flex: "1 1 280px",
  },
  row: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  toggleRow: {
    display: "flex",
    flexDirection: "column",
    paddingTop: 4,
  },
  toggleLabel: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    fontSize: 13,
    fontWeight: 600,
    color: "#ccc",
    cursor: "pointer",
  },
  toggleTextBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: 1,
  },
  helperText: {
    fontSize: 11,
    color: "#7f93ad",
    marginBottom: 4,
  },
  label: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 13,
    fontWeight: 600,
    color: "#ccc",
  },
  value: {
    fontWeight: 400,
    color: "#8cb4ff",
  },
  slider: {
    width: "100%",
    accentColor: "#5a9cff",
    cursor: "pointer",
  },
  checkbox: {
    width: 18,
    height: 18,
    marginTop: 2,
    accentColor: "#5a9cff",
    cursor: "pointer",
    flexShrink: 0,
  },
  boundsRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    color: "#777",
  },
  boundText: {},
  footer: {
    marginTop: 18,
    display: "flex",
    justifyContent: "center",
  },
  resetBtn: {
    background: "#3a4555",
    color: "#ccc",
    border: "1px solid #555",
    borderRadius: 8,
    padding: "8px 20px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
};
